import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ColDef,
  GridOptions,
  GridApi,
  IServerSideGetRowsParams,
  GridReadyEvent,
  IServerSideDatasource,
  IRowNode,
  RowNode,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

// COMPONENTS
import { BusinessAccount } from '@app/core/client/store/client.model';
import {
  AgGridDropdownMenuCellRendererComponent,
  IAgGridDropdownMenuCellRendererParams,
  IAgGridDropdownMenuOption,
} from 'v2/modules/shared/components/ag-grid-dropdown-menu-cell-renderer/ag-grid-dropdown-menu-cell-renderer.component';
import {
  AgGridSearchCompanyAutocompleteCellEditorComponent,
  IAgGridSearchCompanyAutocompleteCellEditorParams,
} from 'v2/modules/shared/components/ag-grid-search-company-autocomplete-cell-editor/ag-grid-search-company-autocomplete-cell-editor.component';

// CONFIGS
import {
  EAppIntegrationAccountCompanyFilter,
  IAppIntegrationAccountCompany,
} from '../../../shared/interfaces/app-integration-account.interface';
import { trackByControlValue, trackById } from '../../../shared/util/track-by.util';

import { SnackBarConfig } from './../../../shared/configs/snackbar.config';

// INTERFACES

// SERVICES
import { AppIntegrationAccountCompaniesApiService } from './../../../shared/services/api/app-integration/app-integration-account-companies-api.service';

// UTILS

@Component({
  selector: 'lum-app-integration-account-manage-companies-grid',
  templateUrl: './app-integration-account-manage-companies-grid.component.html',
  styleUrls: ['./app-integration-account-manage-companies-grid.component.scss'],
})
export class AppIntegrationAccountManageCompaniesGridComponent
  implements OnInit, OnDestroy
{
  columnDefs :ColDef[] = [
    {
      headerCheckboxSelection: true,
      checkboxSelection: (params) => !params.data.lumCompany,
      width: 50,
      flex: 0,
    },
    {
      headerName: 'Company',
      field: 'company',
      valueGetter: (params) => params.data.name || '',
      sortable: false,
      floatingFilter: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
        debounceMs: 1000,
      },
    },
    {
      headerName: 'LUM-COMPANY',
      field: 'lumCompany',
      valueGetter: (params) => params.data.lumCompany?.businessName || '',
      sortable: false,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
        debounceMs: 1000,
      },
      editable: true,
      singleClickEdit: true,
      cellEditorFramework: AgGridSearchCompanyAutocompleteCellEditorComponent,
      cellEditorParams: {
        selectItemEvent: this.onSelectCompany.bind(this),
      } as IAgGridSearchCompanyAutocompleteCellEditorParams,
    },
    {
      headerName: '',
      cellRendererSelector: (params) => {
        if (params.data.lumCompany) {
          return {
            component: 'agGroupCellRenderer',
          };
        }

        return {
          component: AgGridDropdownMenuCellRendererComponent,
          params: {
            options: this.getDropdownOptions(),
            actionSelected: this.handleDropdownAction.bind(this),
          } as IAgGridDropdownMenuCellRendererParams,
        };
      },
      pinned: 'right',
      width: 40,
      lockPosition: true,
      lockVisible: true,
      suppressColumnsToolPanel: true,
      suppressMenu: true,
      filter: false,
      sortable: false,
      resizable: false,
    },
  ];

  gridOptions :GridOptions = {
    rowModelType: 'serverSide',
    pagination: true,
    cacheBlockSize: 50,
    maxBlocksInCache: 50,
    paginationPageSize: 50,
    rowSelection: 'multiple',
    getRowNodeId: (data :IAppIntegrationAccountCompany) => data.id.toString(),
    suppressRowClickSelection: true,
    isRowSelectable: (rowNode) => !rowNode.data.lumCompany,
    sideBar: {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressRowGroups: true,
          },
        },
      ],
      defaultToolPanel: '',
    },
    defaultColDef: {
      suppressSizeToFit: true,
      suppressMenu: true,
      suppressMovable: true,
      sortable: false,
      flex: 1,
      floatingFilter: true,
    },
    onSelectionChanged: this.onSelectionChanged.bind(this),
  };

  accountId :number;
  customFilters :{ [key :string] :string } = {
    mapTo: EAppIntegrationAccountCompanyFilter.ALL,
  };
  eAppIntegrationAccountCompanyFilter = EAppIntegrationAccountCompanyFilter;
  autoMapLoading :boolean = false;
  selectedRowNodeIds :number[] = [];

  private gridApi :GridApi;
  private onDestroy$ = new Subject<void>();

  constructor(
    private appIntegrationAccountCompaniesApiService :AppIntegrationAccountCompaniesApiService,
    private route :ActivatedRoute,
    private router :Router,
    private snackBar :MatSnackBar,
    private cd :ChangeDetectorRef,
  ) {}

  trackByControlValue = trackByControlValue;
  trackById = trackById;

  ngOnInit() :void {
    this.initRouteParams();
    this.accountId = Number(this.route?.parent?.snapshot.paramMap.get('id'));

    if (!this.accountId) {
      this.router.navigate(['settings/app-integration-account-settings']);

      return;
    }
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onGridReady(params :GridReadyEvent) :void {
    params.api.sizeColumnsToFit();

    // Adjust pagination panel margin to prevent overlap with intercom widget
    const paginationPanel = document.querySelector('.ag-paging-panel');
    if (paginationPanel) {
      (paginationPanel as HTMLElement).style.paddingRight = '100px';
    }

    this.gridApi = params.api;
    const datasource = this.getServerSideDatasource();
    this.gridApi.setServerSideDatasource(datasource);
  }

  getServerSideDatasource() :IServerSideDatasource {
    return {
      getRows: (params :IServerSideGetRowsParams) => {
        this.appIntegrationAccountCompaniesApiService
          .get(this.mapRequestParams(params))
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((response) => {
            params.successCallback(response.data, response.meta.total);
          });
      },
    };
  }

  onSelectionChanged(event :SelectionChangedEvent) :void {
    const rowNodes :RowNode[] = [];
    this.gridApi.forEachNode((node :RowNode) => rowNodes.push(node));

    this.selectedRowNodeIds = rowNodes
      .filter((node) => node.isSelected())
      .map((node) => node.data.id);
    this.cd.detectChanges();
  }

  onSelectCompany(
    currentItem :BusinessAccount | null,
    previousItem :BusinessAccount | null,
    node :IRowNode,
  ) :void {
    this.appIntegrationAccountCompaniesApiService
      .update(node.data.id, {
        lumCompanyId: currentItem?.id || null,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.snackBar.open('Successfully updated company', 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });

          this.gridApi.flashCells({
            rowNodes: [node],
            columns: ['lumCompany'],
          });
        },
        error: (error) => {
          this.snackBar.open(error.message || 'Failed to update company', 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });

          node.setDataValue('lumCompany', previousItem);
          this.gridApi.flashCells({
            rowNodes: [node],
            columns: ['lumCompany'],
          });
        },
      });
  }

  onCustomFilterChange(value :string, key :string) :void {
    this.customFilters = {
      ...this.customFilters,
      [key]: value,
    };

    this.reloadGrid();
  }

  onSyncCompanies() :void {
    this.gridApi.showLoadingOverlay();
    this.appIntegrationAccountCompaniesApiService
      .syncCompanies({ appIntegrationAccountId: this.accountId.toString() })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.reloadGrid();
        }),
      )
      .subscribe({
        next: (response) => {
          this.snackBar.open(
            'Sync in progress - this may take a while. Please refresh the page after a few minutes.',
            'Success',
            { duration: SnackBarConfig.DEFAULT.duration },
          );
        },
        error: (error) => {
          this.snackBar.open(error.message || 'Failed to sync companies', 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  onAutoMapCompanies() :void {
    this.gridApi.showLoadingOverlay();
    this.autoMapLoading = true;
    this.appIntegrationAccountCompaniesApiService
      .autoMapCompanies(this.accountId)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.reloadGrid();
          this.autoMapLoading = false;
        }),
      )
      .subscribe({
        next: (response :any) => {
          this.snackBar.open(response.success, 'Info', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
        error: (error) => {
          this.snackBar.open(
            error.message || 'Failed to auto map companies',
            'Fail',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
        },
      });
  }

  onBulkCreateBusinessAccount() :void {
    const payload = {
      appIntegrationAccountId: this.accountId,
      omsCompanyIds: this.selectedRowNodeIds,
    };

    this.gridApi.showLoadingOverlay();
    this.appIntegrationAccountCompaniesApiService
      .bulkCreateBusinessAccount(payload)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.reloadGrid();
        }),
      )
      .subscribe({
        next: (response) => {
          this.snackBar.open('Successfully created business accounts', 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
        error: (error) => {
          this.snackBar.open(
            error.message || 'Failed to create business accounts',
            'Fail',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
        },
      });
  }

  private mapRequestParams(params :IServerSideGetRowsParams) :{
    [param :string] :string | string[];
  } {
    const filterModel = params?.request?.filterModel ?? {};
    const sortModel = params?.request?.sortModel ?? [];

    const relations = 'lumCompany';
    const limit = this.gridOptions.paginationPageSize || 50;
    const page = params?.request?.startRow
      ? params.request.startRow / limit + 1
      : 1;

    let filters :{ [key :string] :string | undefined } = {};
    let sortDirection = 'desc';
    let sortField = 'created_at';

    if (sortModel?.length > 0) {
      sortDirection = sortModel[0].sort;
      sortField = sortModel[0].colId;
    }

    if (Object.keys(filterModel).length > 0) {
      // formats filter model to match the expected format { key: value }
      filters = Object.keys(filterModel).reduce((acc, key) => {
        acc[key] = filterModel[key].filter;

        return acc;
      }, {});
    }

    return {
      limit: limit.toString(),
      page: page.toString(),
      appIntegrationAccountId: this.accountId.toString(),
      synchronous: 'false',
      sortDirection,
      sortField,
      relations,
      ...filters,
      ...this.customFilters,
    };
  }

  private initRouteParams() :void {
    this.accountId = Number(this.route?.parent?.snapshot.paramMap.get('id'));
    if (!this.accountId) {
      this.router.navigate(['settings/app-integration-account-settings']);

      return;
    }
  }

  private reloadGrid() :void {
    const datasource = this.getServerSideDatasource();
    this.gridApi.setServerSideDatasource(datasource);
    this.gridApi.deselectAll();
  }

  private getDropdownOptions() :IAgGridDropdownMenuOption[] {
    return [
      {
        label: 'Create Business Account',
        icon: 'fas fa-list-alt text-info',
        action: 'createBusinessAccount',
        onRowClick: (data) => this.createBusinessAccount(data),
      },
      // add other options here
    ];
  }

  private handleDropdownAction(event :{ action :string; data :any }) {
    switch (event.action) {
      case 'createBusinessAccount':
        this.createBusinessAccount(event.data);
        break;
      // add other cases here
    }
  }

  private createBusinessAccount(data :IAppIntegrationAccountCompany) :void {
    this.gridApi.showLoadingOverlay();

    const omsCompanyId = data.id;
    this.appIntegrationAccountCompaniesApiService
      .createBusinessAccount(omsCompanyId)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.gridApi.hideOverlay();
        }),
      )
      .subscribe({
        next: (response) => {
          this.snackBar.open('Successfully created business account', 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });

          this.updateGridRowByDataId(response);
        },
        error: (error) => {
          this.snackBar.open(
            error.message || 'Failed to create business account',
            'Fail',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
        },
      });
  }

  private updateGridRowByDataId(updatedData :IAppIntegrationAccountCompany) :void {
    const rowNode = this.gridApi.getRowNode(updatedData.id.toString());
    if (rowNode) {
      rowNode.setData(updatedData);
    }
  }
}
